import { useEffect, useState } from "react";
import { Modal } from "../../shared";
import { ImageUploadField } from "../../atoms";

/**
 * Makine tipi oluştur/düzenle + BOM slot editörü (D-058).
 * machineType=null → yeni kayıt. BOM yalnız origin "produced" iken düzenlenir.
 */
export default function MachineTypeModal({
  open,
  machineType,
  allParts = [],
  onClose,
  onSubmit,
  saving,
}) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: machineType?.name || "",
      code: machineType?.code || "",
      origin: machineType?.origin || "produced",
      cupNumber: machineType?.cupNumber || 2,
      isActive: machineType ? machineType.isActive : true,
      // Görsel ayrı endpoint'ten gider (multipart): burada yalnız niyet tutulur.
      image: machineType?.image || null,
      imageFile: null,
      imageCleared: false,
      // BOM: [{ partId, optional }]
      slots: (machineType?.bomSlots || []).map((s) => ({
        partId: s.part?._id || s.part,
        optional: Boolean(s.optional),
      })),
    });
  }, [open, machineType]);

  if (!open || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  /** null = mevcut görseli kaldır, File = yeni görsel yükle. */
  const setImage = (picked) =>
    setForm((f) => ({
      ...f,
      imageFile: picked,
      imageCleared: picked === null,
      image: picked === null ? null : f.image,
    }));

  const toggleSlot = (partId) => {
    const exists = form.slots.find((s) => s.partId === partId);
    set(
      "slots",
      exists
        ? form.slots.filter((s) => s.partId !== partId)
        : [...form.slots, { partId, optional: false }]
    );
  };

  const toggleOptional = (partId) =>
    set(
      "slots",
      form.slots.map((s) =>
        s.partId === partId ? { ...s, optional: !s.optional } : s
      )
    );

  const valid = form.name.trim() && form.code.trim().length >= 2;

  return (
    <Modal open={open} onClose={onClose} maxWidth="lg">
      <Modal.Header
        title={machineType ? "Makine Tipini Düzenle" : "Yeni Makine Tipi"}
        subtitle="Katalog kaydı — makineler montaj tamamlanınca tabletten doğar"
        onClose={onClose}
      />
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Makine Adı</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Örn. 4'LÜ TÜRK KAHVESİ MAKİNESİ"
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Kod</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => set("code", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5))}
                placeholder="M4TK"
                className="w-full border border-input rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Göz Sayısı</label>
              <input
                type="number"
                min={1}
                max={12}
                value={form.cupNumber}
                onChange={(e) => set("cupNumber", Number(e.target.value))}
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
          </div>

          <ImageUploadField
            label="Makine Görseli"
            hint="(tablette montaj ekranında operatöre gösterilir)"
            value={form.image}
            file={form.imageFile}
            onChange={setImage}
            disabled={saving}
          />

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Köken</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => set("origin", "produced")}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                  form.origin === "produced"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
              >
                Üretim (parçalardan montaj)
              </button>
              <button
                type="button"
                onClick={() => set("origin", "imported")}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                  form.origin === "imported"
                    ? "bg-gray-100 text-gray-700 border-gray-300"
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
              >
                İthal (bütün ünite — Espresso)
              </button>
            </div>
          </div>

          {form.origin === "produced" && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                BOM — Ana Parça Slotları{" "}
                <span className="font-normal text-gray-400">
                  (yalnız üst parçalar; alt parçalar üst parçayla gelir)
                </span>
              </label>
              <div className="flex flex-col gap-1.5">
                {allParts.map((p) => {
                  const slot = form.slots.find((s) => s.partId === p._id);
                  return (
                    <div
                      key={p._id}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                        slot ? "bg-indigo-50/50 border-indigo-200" : "bg-white border-gray-200"
                      }`}
                    >
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(slot)}
                          onChange={() => toggleSlot(p._id)}
                          className="w-4 h-4"
                        />
                        {p.name}
                        <span className="font-mono text-[10px] text-gray-400">{p.code}</span>
                        {p.subParts?.length > 0 && (
                          <span className="text-[10px] text-gray-400">
                            ⊃ {p.subParts.map((s) => s.name).join(", ")}
                          </span>
                        )}
                      </label>
                      {slot && (
                        <button
                          type="button"
                          onClick={() => toggleOptional(p._id)}
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                            slot.optional
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}
                        >
                          {slot.optional ? "Opsiyonel" : "Zorunlu"}
                        </button>
                      )}
                    </div>
                  );
                })}
                {allParts.length === 0 && (
                  <span className="text-xs text-gray-400">Önce parça tipleri ekleyin</span>
                )}
              </div>
            </div>
          )}

          {machineType && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set("isActive", e.target.checked)}
                className="w-4 h-4"
              />
              Aktif
            </label>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={onClose}
          className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Vazgeç
        </button>
        <button
          onClick={() => onSubmit(form)}
          disabled={!valid || saving}
          className="px-3.5 py-2 text-sm font-semibold text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50"
        >
          {saving ? "Kaydediliyor..." : machineType ? "Kaydet" : "Oluştur"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
